import { Chip, Skeleton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DateTime } from "luxon";
import { createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import { TbArrowMoveRight } from "react-icons/tb";

import { historyTypes } from "@/constants/historyTypeList";
import { editableOptions } from "@/pages/Plannn/components/table/PlanUpdateDialog";
import { DashboardPassengerBasicInfo } from "@/schemas/dashboard.schemas";
import {
    IPassengerHistory,
    IPassengerHistoryChangeItem,
    companionListSchema,
} from "@/schemas/passenger-history.schemas";

import {
    HistoryChangeItemCompanions,
    HistoryChangeItemCompanionsSkeleton,
    HistoryChangeItemFormatter,
} from "./PassengerHistoryItemFormatters";

function HistoryChanges({ changes }: { changes: IPassengerHistoryChangeItem[] }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2  mt-2 gap-3 p-3 text-xs italic font-normal text-gray-500 border border-gray-200 rounded-lg bg-gray-50 ">
            {changes.map((change, index) => (
                <HistoryChangeItem key={index} {...change} />
            ))}
        </div>
    );
}

function HistoryChangeItemSkeleton({ item }: { item: { companions: boolean; changes: number } }) {
    return item.companions ? (
        <HistoryChangeItemCompanionsSkeleton />
    ) : (
        <div className="grid grid-cols-1 md:grid-cols-2  mt-2 gap-3 p-3 text-xs italic font-normal text-gray-500 border border-gray-200 rounded-lg bg-gray-50 ">
            {Array.from({ length: item.changes }).map((_, index) => (
                <div key={index} className=" flex flex-col   max-w-fit">
                    <div className="text-gray-500">
                        <Skeleton animation="wave" width={60} height={14} variant="text" />
                    </div>
                    <Chip
                        icon={<Skeleton animation="wave" width={20} height={20} variant="circular" />}
                        label={
                            <div className="flex items-center gap-2">
                                <Skeleton animation="wave" width={100} height={22} variant="text" />
                                <TbArrowMoveRight color={grey[500]} fontSize={14} />
                                <Skeleton animation="wave" width={100} height={22} variant="text" />
                            </div>
                        }
                    ></Chip>
                </div>
            ))}
        </div>
    );
}

function HistoryChangeItem({ field, from, to }: IPassengerHistoryChangeItem) {
    const { t } = useTranslation();
    if (!field || (!from && !to)) return null;

    const item = editableOptions[field as keyof typeof editableOptions];
    if (!item) return null;
    if (field === "companions") {
        const isValidFrom = companionListSchema.safeParse(from);
        const isValidTo = companionListSchema.safeParse(to);

        if (!isValidFrom.success || !isValidTo.success) return null;
        return (
            <HistoryChangeItemCompanions from={isValidFrom.data || null} to={isValidTo.data || null} field={field} />
        );
    } else
        return (
            <div className=" flex flex-col   max-w-fit">
                <div className="text-gray-500">{t(field)}</div>
                <Chip
                    icon={createElement(item.icon)}
                    label={
                        <div className="flex items-center gap-2">
                            {field === "cancelled" && !!to ? (
                                "İptal Edildi"
                            ) : (
                                <>
                                    <HistoryChangeItemFormatter field={field} value={from} />
                                    <TbArrowMoveRight fontSize={14} />
                                    <HistoryChangeItemFormatter field={field} value={to} />
                                </>
                            )}
                        </div>
                    }
                ></Chip>
            </div>
        );
}

const historySkeletons = [
    {
        companions: false,
        changes: 2,
    },
    {
        companions: false,
        changes: 0,
    },
    {
        companions: true,
        changes: 0,
    },
    {
        companions: false,
        changes: 1,
    },
];

export function HistoryItemSkeleton() {
    return historySkeletons.map((item, index) => (
        <li key={index} className="mb-10 ms-6 relative">
            <div className="absolute flex items-center justify-center w-8 h-8  shadow rounded-full -left-10  top-1 ring-1  ring-gray-300 bg-white ">
                <Skeleton animation="wave" width={32} height={32} variant="circular" />
            </div>
            <div className="grid grid-cols-[auto,100px] relative pr-5 p-4 bg-white border border-gray-200 rounded-lg shadow-xs  ">
                <Skeleton animation="wave" width="70%" height={22} variant="text" />

                <div className=" absolute right-4 top-4 text-left text-xs font-normal text-gray-400 ">
                    <Skeleton animation="wave" width={70} height={16} variant="text" />
                </div>
                <div className=" col-span-full">
                    {(!!item.changes || item.companions) && <HistoryChangeItemSkeleton item={item} />}
                </div>
            </div>
        </li>
    ));
}

function HistortItemWrapper({
    children,
    createdAt,
    icon,
    text,
    bg,
    detail,
}: {
    children: React.ReactNode;
    createdAt: string;
    icon: () => IconType;
    text: string;
    bg: string;
    detail: React.ReactNode;
}) {
    const time = DateTime.fromISO(createdAt).setLocale("tr");
    const relativeTime = time.toRelative({ locale: "tr" });
    return (
        <div className="mb-10 ms-6 relative  ">
            <span
                style={{ backgroundColor: bg }}
                className="absolute flex items-center justify-center w-8 h-8  shadow rounded-full  -left-10 top-1 ring-1  ring-gray-300 "
            >
                {createElement(icon(), {
                    color: text,
                    size: 24,
                })}
            </span>
            <div className="grid grid-cols-[auto,100px] relative pr-5 p-4 bg-white  border border-gray-200 rounded-lg shadow  ">
                {children}
                <div className=" absolute right-4 top-4 text-left text-xs font-normal text-gray-400 ">
                    <Tooltip title={time.toFormat("dd MMMM yyyy, HH:mm")}>
                        <span>{relativeTime}</span>
                    </Tooltip>
                </div>
                <div className=" col-span-full">{detail}</div>
            </div>
        </div>
    );
}

export default function ({ item, passenger }: { item: IPassengerHistory; passenger?: DashboardPassengerBasicInfo }) {
    const { createdAt, changes, type } = item;
    if (!changes) return null;
    const info = historyTypes.find((item) => item.value === type);
    if (!info) return null;
    const { render, component, text, bg, useChanges } = info;
    const detail = useMemo(() => (useChanges ? <HistoryChanges changes={changes} /> : null), [useChanges, changes]);

    return (
        <HistortItemWrapper detail={detail} icon={component} text={text} bg={bg} createdAt={createdAt}>
            <div>
                {createElement(render, {
                    item,
                    info,
                    passenger,
                })}
            </div>
        </HistortItemWrapper>
    );
}
